import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Star, Search, X, Plus, Minus,
  Heart, Truck, RotateCcw, Shield, Zap,
  SlidersHorizontal, ChevronRight, ChevronDown, ChevronUp,
  BadgePercent, PackageCheck,
} from "lucide-react";
import { handleAddToCartGlobal } from "../common/Header";
import { SHOPPING_PRODUCTS } from "../../config/pricing";

// ─── Animations injected once ────────────────────────────────────────────────
const ANIM_STYLE = `
@keyframes slideInRight { from { transform:translateX(120%); opacity:0; } to { transform:translateX(0); opacity:1; } }
@keyframes fadeOut      { from { opacity:1; } to { opacity:0; } }
@keyframes pulse        { 0%,100% { transform:scale(1); } 50% { transform:scale(1.08); } }
@keyframes ticker       { from { transform:translateX(0); } to { transform:translateX(-50%); } }
.shop-card:hover { box-shadow:0 16px 40px rgba(13,110,253,0.18) !important; transform:translateY(-4px); }
.shop-card { transition: box-shadow 0.22s ease, transform 0.22s ease; }
.heart-btn:hover { transform:scale(1.2); }
.cat-card:hover { transform:translateY(-3px); box-shadow:0 10px 28px rgba(0,0,0,0.13) !important; }
.cat-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
.add-btn-anim:active { transform:scale(0.96); }
`;

// ─── Category metadata ────────────────────────────────────────────────────────
// `image` is a temporary stock photo per category (real per-product photos to
// be added later) — used for both the category tiles and every product card
// within that category, replacing the old emoji + CSS-gradient placeholder.
const CATEGORY_META = {
  "Cleaners":       { image:"https://images.unsplash.com/photo-1576503276236-71dd8ad5b0b9?auto=format&fit=crop&w=500&q=70", color:"#fef9c3", accent:"#ca8a04", light:"#fefce8", count:5 },
  "Dishwashing":    { image:"https://images.unsplash.com/photo-1590610994353-7b0e7546e681?auto=format&fit=crop&w=500&q=70", color:"#dbeafe", accent:"#2563eb", light:"#eff6ff", count:3 },
  "Floor & Surface":{ image:"https://images.unsplash.com/photo-1758273238594-9a9d6c20eaa2?auto=format&fit=crop&w=500&q=70", color:"#d1fae5", accent:"#059669", light:"#ecfdf5", count:3 },
  "Laundry":        { image:"https://images.unsplash.com/photo-1649005011845-ef225c89da86?auto=format&fit=crop&w=500&q=70", color:"#ede9fe", accent:"#7c3aed", light:"#faf5ff", count:3 },
  "Air Fresheners": { image:"https://images.unsplash.com/photo-1778530207659-0574c49eca07?auto=format&fit=crop&w=500&q=70", color:"#fce7f3", accent:"#db2777", light:"#fdf2f8", count:3 },
  "Sanitizers":     { image:"https://images.unsplash.com/photo-1608564348103-2b78891150cf?auto=format&fit=crop&w=500&q=70", color:"#fee2e2", accent:"#dc2626", light:"#fef2f2", count:2 },
  "Paper & Tissue": { image:"https://images.unsplash.com/photo-1598046937985-11c320dfd379?auto=format&fit=crop&w=500&q=70", color:"#f3f4f6", accent:"#6b7280", light:"#f9fafb", count:2 },
};

const fallbackImage =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=500&q=70";

const ALL_CATS = ["All", ...Object.keys(CATEGORY_META)];

// ─── Product catalogue ────────────────────────────────────────────────────────
const PRODUCTS = [
  { id:"shop-001", name:"Multi-Surface Cleaning Liquid", subtitle:"Kills 99.9% germs — 1 L", category:"Cleaners",       ...SHOPPING_PRODUCTS["shop-001"], rating:4.5, reviews:312, badge:"Best Seller", description:"All-purpose cleaner for kitchen counters, tiles, sinks and more. Safe on most surfaces." },
  { id:"shop-002", name:"Bathroom Cleaner Gel",          subtitle:"Clings to surfaces, no drip — 750 ml", category:"Cleaners", ...SHOPPING_PRODUCTS["shop-002"], rating:4.3, reviews:198, badge:null, description:"Thick gel formula that clings to toilet bowls, tiles and drainage pipes." },
  { id:"shop-003", name:"Kitchen Degreaser Spray",       subtitle:"Foam action, instant cut — 500 ml", category:"Cleaners",  ...SHOPPING_PRODUCTS["shop-003"], rating:4.6, reviews:245, badge:"New", description:"Instant foam that cuts through tough grease, oil splatter and burnt residue." },
  { id:"shop-004", name:"Toilet Cleaner Liquid",         subtitle:"Under-rim nozzle, fights stains — 750 ml", category:"Cleaners", ...SHOPPING_PRODUCTS["shop-004"], rating:4.4, reviews:408, badge:"Best Seller", description:"Angled nozzle delivers gel under the rim to clean the hardest-to-reach spots." },
  { id:"shop-005", name:"Disinfectant All-Purpose Spray",subtitle:"Kills viruses & bacteria — 500 ml", category:"Cleaners",  ...SHOPPING_PRODUCTS["shop-005"], rating:4.7, reviews:531, badge:"Top Rated", description:"Hospital-grade disinfectant safe for home use on all hard surfaces." },
  { id:"shop-006", name:"Dishwash Liquid — Lime Fresh",  subtitle:"Gentle on hands, tough on grease — 1 L", category:"Dishwashing", ...SHOPPING_PRODUCTS["shop-006"], rating:4.7, reviews:520, badge:"Best Seller", description:"Concentrated formula that leaves dishes sparkling clean with a fresh lime scent." },
  { id:"shop-007", name:"Dishwasher Powder",             subtitle:"For automatic dishwashers — 1 kg", category:"Dishwashing", ...SHOPPING_PRODUCTS["shop-007"], rating:4.4, reviews:134, badge:null, description:"Dissolves food residue at low temperatures and leaves dishes spot-free." },
  { id:"shop-008", name:"Dish Scrub Paste",              subtitle:"Heavy-duty grease remover — 500 g", category:"Dishwashing", ...SHOPPING_PRODUCTS["shop-008"],  rating:4.2, reviews:89, badge:null, description:"Soda-based paste that removes stubborn burnt-on food without scratching." },
  { id:"shop-009", name:"Floor Cleaner — Lavender",      subtitle:"Long fragrance, kills bacteria — 2 L", category:"Floor & Surface", ...SHOPPING_PRODUCTS["shop-009"], rating:4.5, reviews:388, badge:null, description:"Leaves floors hygienically clean and fills the room with fresh lavender scent." },
  { id:"shop-010", name:"Glass & Mirror Cleaner",        subtitle:"Streak-free shine — 500 ml", category:"Floor & Surface", ...SHOPPING_PRODUCTS["shop-010"], rating:4.6, reviews:211, badge:"New", description:"Quick-drying formula for mirrors, windows and any glass surface." },
  { id:"shop-011", name:"Marble & Granite Cleaner",      subtitle:"No acid, safe on stone — 1 L", category:"Floor & Surface", ...SHOPPING_PRODUCTS["shop-011"], rating:4.5, reviews:147, badge:null, description:"pH-neutral cleaner that restores shine to natural stone without etching." },
  { id:"shop-012", name:"Laundry Detergent Powder",      subtitle:"Deep clean, all fabric types — 2 kg", category:"Laundry", ...SHOPPING_PRODUCTS["shop-012"], rating:4.8, reviews:672, badge:"Top Rated", description:"Removes tough stains and leaves clothes bright, fresh and perfectly clean." },
  { id:"shop-013", name:"Liquid Fabric Softener",        subtitle:"Silky, fragrant clothes — 1 L", category:"Laundry", ...SHOPPING_PRODUCTS["shop-013"], rating:4.4, reviews:143, badge:null, description:"Reduces static and leaves fabrics feeling silky smooth after every wash cycle." },
  { id:"shop-014", name:"Stain Remover Spray",           subtitle:"Lifts stains in 60 seconds — 400 ml", category:"Laundry", ...SHOPPING_PRODUCTS["shop-014"], rating:4.6, reviews:264, badge:"New", description:"Enzyme-based formula that lifts coffee, curry, blood and grass stains instantly." },
  { id:"shop-015", name:"Air Freshener — Ocean Breeze",  subtitle:"Instant odour elimination — 300 ml", category:"Air Fresheners", ...SHOPPING_PRODUCTS["shop-015"], rating:4.3, reviews:290, badge:null, description:"Neutralises bad odours and leaves a refreshing ocean breeze scent all day." },
  { id:"shop-016", name:"Gel Air Freshener — Citrus",    subtitle:"Lasts up to 30 days — 150 g", category:"Air Fresheners", ...SHOPPING_PRODUCTS["shop-016"], rating:4.5, reviews:167, badge:null, description:"Slow-release gel that keeps spaces smelling citrus-fresh for up to a month." },
  { id:"shop-017", name:"Car Air Freshener — Vanilla",   subtitle:"Long-lasting vent clip — 1 pc", category:"Air Fresheners", ...SHOPPING_PRODUCTS["shop-017"],  rating:4.4, reviews:201, badge:null, description:"Vent-mounted clip releases consistent vanilla fragrance for up to 60 days." },
  { id:"shop-018", name:"Hand Sanitizer Gel",            subtitle:"70% alcohol, moisturising — 500 ml", category:"Sanitizers", ...SHOPPING_PRODUCTS["shop-018"], rating:4.7, reviews:834, badge:"Best Seller", description:"WHO-formula sanitizer with aloe vera that cleans hands without drying them." },
  { id:"shop-019", name:"Surface Disinfectant Wipes",    subtitle:"100 sheets, pre-moistened", category:"Sanitizers", ...SHOPPING_PRODUCTS["shop-019"], rating:4.5, reviews:312, badge:null, description:"Ready-to-use antibacterial wipes for quick disinfection of any hard surface." },
  { id:"shop-020", name:"Kitchen Paper Towels",          subtitle:"2-ply absorbent, 60 sheets × 2", category:"Paper & Tissue", ...SHOPPING_PRODUCTS["shop-020"], rating:4.3, reviews:178, badge:null, description:"Heavy-duty 2-ply paper that absorbs spills fast and stays intact when wet." },
  { id:"shop-021", name:"Facial Tissue Box",             subtitle:"3-ply soft, 200 pulls × 2", category:"Paper & Tissue", ...SHOPPING_PRODUCTS["shop-021"], rating:4.5, reviews:256, badge:null, description:"Ultra-soft 3-ply facial tissue that's gentle on skin and hypoallergenic." },
];

const SORT_OPTIONS = [
  { value:"default",    label:"Featured"          },
  { value:"price-asc",  label:"Price: Low → High" },
  { value:"price-desc", label:"Price: High → Low" },
  { value:"rating",     label:"Top Rated"         },
  { value:"discount",   label:"Best Discount"     },
];

const PRICE_PRESETS = [
  { label:"Under ₹100", max:100 },
  { label:"Under ₹200", max:200 },
  { label:"Under ₹300", max:300 },
  { label:"Any price",  max:Infinity },
];

const BADGE_CONFIG = {
  "Best Seller":{ bg:"#fef3c7", color:"#92400e" },
  "New":        { bg:"#d1fae5", color:"#065f46" },
  "Top Rated":  { bg:"#ede9fe", color:"#5b21b6" },
};

const discount = (p) => Math.round(((p.mrp - p.price) / p.mrp) * 100);

// ─── Sub-components ───────────────────────────────────────────────────────────

const Stars = ({ rating, size = 13 }) => (
  <span style={{ display:"flex", alignItems:"center", gap:2 }}>
    {[1,2,3,4,5].map((n) => (
      <Star key={n} size={size}
        fill={n <= Math.round(rating) ? "#f59e0b" : "none"}
        stroke="#f59e0b" strokeWidth={1.5}
      />
    ))}
    <span style={{ fontSize:size, color:"#6b7280", marginLeft:3 }}>{rating.toFixed(1)}</span>
  </span>
);

const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  return (
    <div style={{
      position:"fixed", bottom:28, right:28, zIndex:9999,
      background: toast.type === "wish" ? "#1f2937" : "#059669",
      color:"#fff", borderRadius:14, padding:"14px 20px",
      display:"flex", alignItems:"center", gap:12,
      boxShadow:"0 8px 32px rgba(0,0,0,0.22)",
      animation:"slideInRight 0.3s ease",
      fontSize:15, fontWeight:600, maxWidth:320,
    }}>
      {toast.type === "wish"
        ? <Heart size={20} fill="#fff" stroke="#fff"/>
        : <PackageCheck size={20}/>}
      <span style={{ flex:1 }}>{toast.message}</span>
      <button onClick={onClose} style={{ background:"none", border:"none", color:"#fff", cursor:"pointer", opacity:0.7, padding:2 }}>
        <X size={16}/>
      </button>
    </div>
  );
};

const ProductCard = ({ product, cartQty, onAdd, onQty, wishlisted, onWish }) => {
  const meta = CATEGORY_META[product.category] || {};
  const pct  = discount(product);
  const bc   = BADGE_CONFIG[product.badge];
  const qty  = cartQty[product.id] || 0;

  return (
    <div className="shop-card" style={S.card}>
      {/* Wishlist */}
      <button
        className="heart-btn"
        style={{ ...S.heartBtn, color: wishlisted ? "#ef4444" : "#d1d5db" }}
        onClick={() => onWish(product)}
        title={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
      >
        <Heart size={18} fill={wishlisted ? "#ef4444" : "none"} stroke={wishlisted ? "#ef4444" : "#d1d5db"}/>
      </button>

      {/* Discount pill */}
      {pct > 0 && (
        <div style={S.discountPill}>{pct}% OFF</div>
      )}

      {/* Image area */}
      <div style={{ ...S.imgArea, background: meta.light || "#f8fafc" }}>
        <img
          src={meta.image}
          alt={product.name}
          style={S.productImage}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
        />
      </div>

      {/* Badge */}
      {product.badge && bc && (
        <div style={{ ...S.badge, background:bc.bg, color:bc.color }}>{product.badge}</div>
      )}

      <div style={S.cardBody}>
        <p style={{ ...S.catTag, color:meta.accent || "#003c99" }}>{product.category}</p>
        <h3 style={S.cardTitle}>{product.name}</h3>
        <p style={S.cardSub}>{product.subtitle}</p>
        <p style={S.cardDesc}>{product.description}</p>

        {/* Rating */}
        <div style={{ marginTop:8 }}>
          <Stars rating={product.rating}/>
          <span style={{ fontSize:12, color:"#9ca3af", marginTop:2, display:"block" }}>
            {product.reviews.toLocaleString()} reviews
          </span>
        </div>

        {/* Price row */}
        <div style={S.priceRow}>
          <div>
            <span style={S.price}>₹{product.price}</span>
            <span style={S.mrp}>₹{product.mrp}</span>
          </div>
          {/* Cart control */}
          {qty === 0 ? (
            <button
              className="add-btn-anim"
              style={S.addBtn}
              onClick={() => onAdd(product)}
            >
              <ShoppingCart size={15}/> Add
            </button>
          ) : (
            <div style={S.qtyControl}>
              <button style={S.qtyBtn} onClick={() => onQty(product, qty - 1)}>
                <Minus size={14}/>
              </button>
              <span style={S.qtyNum}>{qty}</span>
              <button style={S.qtyBtn} onClick={() => onQty(product, qty + 1)}>
                <Plus size={14}/>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ShoppingPage = () => {
  const navigate = useNavigate();

  const [activeCat,  setActiveCat]  = useState("All");
  const [sortBy,     setSortBy]     = useState("default");
  const [maxPrice,   setMaxPrice]   = useState(Infinity);
  const [minRating,  setMinRating]  = useState(0);
  const [cartQty,    setCartQty]    = useState({});
  const [wishlist,   setWishlist]   = useState(new Set());
  const [toast,      setToast]      = useState(null);
  const [filterOpen, setFilterOpen] = useState(true);
  const [sortOpen,   setSortOpen]   = useState(false);
  const toastTimer = React.useRef(null);

  // inject animation CSS once
  useEffect(() => {
    const id = "shop-anim-style";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = ANIM_STYLE;
      document.head.appendChild(el);
    }
  }, []);

  const showToast = useCallback((message, type = "cart") => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Filtering & sorting ──
  const filtered = useMemo(() => {
    let list = PRODUCTS.filter((p) => {
      if (activeCat !== "All" && p.category !== activeCat) return false;
      if (p.price > maxPrice) return false;
      if (p.rating < minRating) return false;
      return true;
    });
    switch (sortBy) {
      case "price-asc":  list = [...list].sort((a, b) => a.price - b.price); break;
      case "price-desc": list = [...list].sort((a, b) => b.price - a.price); break;
      case "rating":     list = [...list].sort((a, b) => b.rating - a.rating); break;
      case "discount":   list = [...list].sort((a, b) => discount(b) - discount(a)); break;
      default: break;
    }
    return list;
  }, [activeCat, maxPrice, minRating, sortBy]);

  // active filter count for badge
  const activeFilters = [
    activeCat !== "All",
    maxPrice !== Infinity,
    minRating > 0,
  ].filter(Boolean).length;

  // ── Handlers ──
  const handleAdd = useCallback(async (product) => {
    const item = {
      id: product.id, title: product.name, name: product.name,
      subtitle: product.subtitle, price: product.price,
      image: CATEGORY_META[product.category]?.image || fallbackImage,
      category: "Shopping — " + product.category,
    };
    const ok = await handleAddToCartGlobal(item, navigate, "/shopping");
    if (ok !== false) {
      setCartQty((prev) => ({ ...prev, [product.id]: 1 }));
      showToast(`"${product.name}" added to cart!`);
    }
  }, [navigate, showToast]);

  const handleQty = useCallback(async (product, newQty) => {
    if (newQty <= 0) {
      setCartQty((prev) => { const n = { ...prev }; delete n[product.id]; return n; });
      showToast(`"${product.name}" removed from cart.`);
    } else {
      setCartQty((prev) => ({ ...prev, [product.id]: newQty }));
    }
  }, [showToast]);

  const handleWish = useCallback((product) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
        showToast(`Removed from wishlist.`, "wish");
      } else {
        next.add(product.id);
        showToast(`"${product.name}" saved to wishlist!`, "wish");
      }
      return next;
    });
  }, [showToast]);

  const clearFilters = () => {
    setActiveCat("All"); setMaxPrice(Infinity); setMinRating(0);
  };

  const totalInCart = Object.values(cartQty).reduce((s, q) => s + q, 0);

  return (
    <div style={{ fontFamily:"Arial, sans-serif", background:"#f4f5f9", minHeight:"100vh" }}>

      {/* ── Promo ticker ── */}
      <div style={S.ticker}>
        <div style={S.tickerInner}>
          {[
            "Free delivery on orders above ₹499",
            "Use code CLEAN10 for extra 10% off",
            "Buy 3 get 1 FREE on Laundry range",
            "Flash sale: Sanitizers up to 35% off",
            "5000+ happy customers this month",
            "Free delivery on orders above ₹499",
            "Use code CLEAN10 for extra 10% off",
            "Buy 3 get 1 FREE on Laundry range",
            "Flash sale: Sanitizers up to 35% off",
            "5000+ happy customers this month",
          ].join("   ·   ")}
        </div>
      </div>

      {/* ── Page title ── */}
      <h1 style={S.pageTitle}>Home Cleaning &amp; Care</h1>

      {/* ── Category visual cards ── */}
      <div style={S.catSection}>
        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Shop by Category</h2>
          <span style={{ fontSize:14, color:"#6b7280" }}>7 categories</span>
        </div>
        <div style={S.catGrid}>
          {Object.entries(CATEGORY_META).map(([name, meta]) => (
            <button
              key={name}
              className="cat-card"
              style={{
                ...S.catCard,
                background: activeCat === name
                  ? `linear-gradient(135deg, ${meta.accent}22, ${meta.color})`
                  : "#fff",
                borderColor: activeCat === name ? meta.accent : "#e5e7eb",
                borderWidth: activeCat === name ? 2 : 1,
              }}
              onClick={() => setActiveCat(activeCat === name ? "All" : name)}
            >
              <img
                src={meta.image}
                alt={name}
                style={{ width:40, height:40, borderRadius:"50%", objectFit:"cover" }}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
              />
              <span style={{ fontSize:13, fontWeight:700, color: activeCat === name ? meta.accent : "#374151", marginTop:6 }}>{name}</span>
              <span style={{ fontSize:11, color:"#9ca3af" }}>{meta.count} items</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Body: sidebar + grid ── */}
      <div style={S.body}>

        {/* Sidebar */}
        <aside style={{ ...S.sidebar, display: filterOpen ? "flex" : "none" }}>
          <div style={S.sidebarHeader}>
            <span style={{ fontWeight:700, fontSize:16, display:"flex", alignItems:"center", gap:6 }}>
              <SlidersHorizontal size={16} color="#0d6efd"/> Filters
            </span>
            {activeFilters > 0 && (
              <button style={S.clearAllBtn} onClick={clearFilters}>
                Clear all ({activeFilters})
              </button>
            )}
          </div>

          {/* Category filter */}
          <FilterSection title="Category">
            {ALL_CATS.map((cat) => {
              const cnt = cat === "All" ? PRODUCTS.length : PRODUCTS.filter((p) => p.category === cat).length;
              return (
                <label key={cat} style={S.checkRow}>
                  <input
                    type="radio" name="cat" checked={activeCat === cat}
                    onChange={() => setActiveCat(cat)}
                    style={{ accentColor:"#0d6efd" }}
                  />
                  <span style={{ flex:1, fontSize:14, color:"#374151" }}>
                    {cat}
                  </span>
                  <span style={S.filterCount}>{cnt}</span>
                </label>
              );
            })}
          </FilterSection>

          {/* Price filter */}
          <FilterSection title="Price Range">
            {PRICE_PRESETS.map((pr) => (
              <label key={pr.label} style={S.checkRow}>
                <input
                  type="radio" name="price" checked={maxPrice === pr.max}
                  onChange={() => setMaxPrice(pr.max)}
                  style={{ accentColor:"#0d6efd" }}
                />
                <span style={{ fontSize:14, color:"#374151" }}>{pr.label}</span>
              </label>
            ))}
          </FilterSection>

          {/* Rating filter */}
          <FilterSection title="Minimum Rating">
            {[4.5, 4, 3.5, 0].map((r) => (
              <label key={r} style={S.checkRow}>
                <input
                  type="radio" name="rating" checked={minRating === r}
                  onChange={() => setMinRating(r)}
                  style={{ accentColor:"#0d6efd" }}
                />
                <span style={{ fontSize:14, color:"#374151", display:"flex", alignItems:"center", gap:4 }}>
                  {r === 0 ? "All ratings" : (
                    <><Stars rating={r} size={12}/> &amp; above</>
                  )}
                </span>
              </label>
            ))}
          </FilterSection>

          {/* Wishlist link */}
          {wishlist.size > 0 && (
            <div style={S.wishlistBox}>
              <Heart size={16} color="#ef4444" fill="#ef4444"/>
              <span style={{ fontSize:14, color:"#374151", flex:1 }}>
                {wishlist.size} item{wishlist.size > 1 ? "s" : ""} wishlisted
              </span>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div style={{ flex:1, minWidth:0 }}>

          {/* Sort / result bar */}
          <div style={S.sortBar}>
            {/* Filter toggle (mobile-style) */}
            <button style={S.filterToggle} onClick={() => setFilterOpen((o) => !o)}>
              <SlidersHorizontal size={15}/>
              {filterOpen ? "Hide" : "Filters"}
              {activeFilters > 0 && <span style={S.filterBadge}>{activeFilters}</span>}
            </button>

            <span style={{ fontSize:14, color:"#6b7280", flex:1 }}>
              <strong style={{ color:"#111827" }}>{filtered.length}</strong> product{filtered.length !== 1 ? "s" : ""}
              {activeCat !== "All" && <> in <strong>{activeCat}</strong></>}
            </span>

            {/* Sort dropdown */}
            <div style={{ position:"relative" }}>
              <button style={S.sortBtn} onClick={() => setSortOpen((o) => !o)}>
                <span>{SORT_OPTIONS.find((o) => o.value === sortBy)?.label}</span>
                {sortOpen ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
              </button>
              {sortOpen && (
                <div style={S.sortDropdown}>
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      style={{ ...S.sortOption, background: sortBy === opt.value ? "#dbeafe" : "transparent", color: sortBy === opt.value ? "#0d6efd" : "#374151", fontWeight: sortBy === opt.value ? 700 : 400 }}
                      onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart pill — there's no standalone cart page, so this opens
                the checkout summary directly (items were just added via
                handleAdd()'s login-gated handleAddToCartGlobal, so the
                user is already logged in at this point). */}
            {totalInCart > 0 && (
              <button style={S.cartPill} onClick={() => navigate("/checkout-schedule/summary")}>
                <ShoppingCart size={16}/> {totalInCart} in cart
              </button>
            )}
          </div>

          {/* Active filter chips */}
          {activeFilters > 0 && (
            <div style={S.chipRow}>
              {activeCat !== "All" && (
                <FilterChip label={activeCat} onRemove={() => setActiveCat("All")}/>
              )}
              {maxPrice !== Infinity && (
                <FilterChip label={`Under ₹${maxPrice}`} onRemove={() => setMaxPrice(Infinity)}/>
              )}
              {minRating > 0 && (
                <FilterChip label={`${minRating}★ & above`} onRemove={() => setMinRating(0)}/>
              )}
            </div>
          )}

          {/* Product grid */}
          {filtered.length === 0 ? (
            <EmptyState onClear={clearFilters}/>
          ) : (
            <div style={S.grid}>
              {filtered.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  cartQty={cartQty}
                  onAdd={handleAdd}
                  onQty={handleQty}
                  wishlisted={wishlist.has(p.id)}
                  onWish={handleWish}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Trust bar ── */}
      <div style={S.trustBar}>
        {[
          { icon:<Truck size={26} color="#0d6efd"/>,       title:"Free Delivery",      sub:"On orders above ₹499"       },
          { icon:<RotateCcw size={26} color="#059669"/>,   title:"Easy Returns",       sub:"7-day hassle-free returns"  },
          { icon:<Shield size={26} color="#dc2626"/>,      title:"Secure Payment",     sub:"100% safe transactions"     },
          { icon:<BadgePercent size={26} color="#d97706"/>,title:"Best Prices",        sub:"Price-match guarantee"      },
          { icon:<PackageCheck size={26} color="#003c99"/>,title:"Quality Assured",    sub:"Tested &amp; certified products" },
        ].map((t) => (
          <div key={t.title} style={S.trustItem}>
            <div style={S.trustIcon}>{t.icon}</div>
            <div>
              <p style={S.trustTitle}>{t.title}</p>
              <p style={S.trustSub} dangerouslySetInnerHTML={{ __html: t.sub }}/>
            </div>
          </div>
        ))}
      </div>

      {/* Toast */}
      <Toast toast={toast} onClose={() => setToast(null)}/>
    </div>
  );
};

// ─── Small helper components ──────────────────────────────────────────────────

const FilterSection = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom:"1px solid #f3f4f6", paddingBottom:14, marginBottom:14 }}>
      <button
        style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", background:"none", border:"none", cursor:"pointer", padding:"0 0 10px", fontSize:14, fontWeight:700, color:"#111827" }}
        onClick={() => setOpen((o) => !o)}
      >
        {title}
        {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
      </button>
      {open && <div style={{ display:"flex", flexDirection:"column", gap:8 }}>{children}</div>}
    </div>
  );
};

const FilterChip = ({ label, onRemove }) => (
  <span style={S.chip}>
    {label}
    <button style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", lineHeight:1 }} onClick={onRemove}>
      <X size={13} color="#0d6efd"/>
    </button>
  </span>
);

const EmptyState = ({ onClear }) => (
  <div style={{ textAlign:"center", padding:"80px 36px" }}>
    <Search size={56} color="#9ca3af" style={{ marginBottom:16 }}/>
    <h3 style={{ fontSize:22, color:"#111827", margin:"0 0 8px" }}>No products found</h3>
    <p style={{ color:"#6b7280", marginBottom:24 }}>Try adjusting your filters.</p>
    <button style={S.clearFiltersBtn} onClick={onClear}>Clear All Filters</button>
  </div>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  // Ticker
  ticker: { background:"linear-gradient(90deg,#0d6efd,#003c99)", color:"#fff", overflow:"hidden", fontSize:13, fontWeight:500, padding:"6px 0", whiteSpace:"nowrap" },
  tickerInner: { display:"inline-block", animation:"ticker 30s linear infinite", paddingLeft:"100%" },

  // Hero
  pageTitle: { margin:"20px 36px 0", fontSize:26, fontWeight:900, color:"#111827" },

  // Category visual cards
  catSection: { padding:"18px 36px 4px", background:"#fff" },
  sectionHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 },
  sectionTitle:  { margin:0, fontSize:20, fontWeight:800, color:"#111827" },
  catGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))", gap:10, paddingBottom:16 },
  catCard: { display:"flex", flexDirection:"column", alignItems:"center", padding:"12px 10px 10px", borderRadius:16, border:"1px solid #e5e7eb", cursor:"pointer", gap:3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" },

  // Body layout
  body: { display:"flex", gap:0, alignItems:"flex-start", padding:"0 36px 60px", marginTop:24 },

  // Sidebar
  sidebar: { width:230, flexShrink:0, background:"#fff", borderRadius:18, padding:"20px 18px", marginRight:24, flexDirection:"column", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", position:"sticky", top:20, maxHeight:"calc(100vh - 80px)", overflowY:"auto" },
  sidebarHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, paddingBottom:14, borderBottom:"1px solid #f3f4f6" },
  clearAllBtn: { fontSize:12, fontWeight:600, color:"#ef4444", background:"#fef2f2", border:"none", borderRadius:6, padding:"4px 10px", cursor:"pointer" },
  checkRow: { display:"flex", alignItems:"center", gap:8, cursor:"pointer" },
  filterCount: { fontSize:12, color:"#9ca3af", background:"#f3f4f6", borderRadius:999, padding:"1px 7px" },
  wishlistBox: { display:"flex", alignItems:"center", gap:8, background:"#fff1f2", borderRadius:10, padding:"10px 12px", marginTop:8 },

  // Sort / result bar
  sortBar: { display:"flex", alignItems:"center", gap:12, background:"#fff", borderRadius:14, padding:"12px 18px", marginBottom:12, flexWrap:"wrap", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" },
  filterToggle: { display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:10, border:"1px solid #d1d5db", background:"#fff", fontSize:14, fontWeight:600, cursor:"pointer", color:"#374151", position:"relative" },
  filterBadge: { position:"absolute", top:-6, right:-6, minWidth:18, height:18, background:"#0d6efd", color:"#fff", borderRadius:999, fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" },
  sortBtn: { display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:10, border:"1px solid #d1d5db", background:"#fff", fontSize:14, fontWeight:600, cursor:"pointer", color:"#374151", whiteSpace:"nowrap" },
  sortDropdown: { position:"absolute", top:"110%", right:0, background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", zIndex:500, minWidth:200, overflow:"hidden" },
  sortOption: { display:"block", width:"100%", padding:"10px 16px", fontSize:14, border:"none", cursor:"pointer", textAlign:"left" },
  cartPill: { display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:999, border:"none", background:"linear-gradient(135deg,#0d6efd,#003c99)", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" },

  // Active chips
  chipRow: { display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 },
  chip: { display:"inline-flex", alignItems:"center", gap:6, background:"#dbeafe", color:"#0d6efd", fontSize:13, fontWeight:600, padding:"5px 10px 5px 12px", borderRadius:999 },

  // Product grid
  grid: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(240px, 1fr))", gap:20 },

  // Product card
  card: { background:"#fff", borderRadius:20, overflow:"hidden", display:"flex", flexDirection:"column", position:"relative", boxShadow:"0 2px 10px rgba(0,0,0,0.07)" },
  heartBtn: { position:"absolute", top:12, right:12, zIndex:10, background:"rgba(255,255,255,0.9)", border:"none", borderRadius:999, width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", backdropFilter:"blur(4px)", transition:"transform 0.15s" },
  discountPill: { position:"absolute", top:12, left:12, zIndex:10, background:"linear-gradient(135deg,#dc2626,#ef4444)", color:"#fff", fontSize:11, fontWeight:800, padding:"3px 9px", borderRadius:999, letterSpacing:0.5 },
  imgArea: { height:160, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden" },
  productImage: { width:"100%", height:"100%", objectFit:"cover" },
  badge: { position:"absolute", top:166, left:16, fontSize:10, fontWeight:800, padding:"3px 9px", borderRadius:999, letterSpacing:0.5 },
  cardBody: { padding:"22px 18px 18px", flex:1, display:"flex", flexDirection:"column" },
  catTag: { margin:"0 0 5px", fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8 },
  cardTitle: { margin:"0 0 4px", fontSize:15, fontWeight:800, color:"#111827", lineHeight:1.3 },
  cardSub: { margin:"0 0 4px", fontSize:12, color:"#6b7280" },
  cardDesc: { margin:"0 0 auto", fontSize:12, color:"#9ca3af", lineHeight:1.5 },
  priceRow: { display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:14, gap:8 },
  price: { fontSize:22, fontWeight:900, color:"#111827", display:"block" },
  mrp:   { fontSize:13, color:"#9ca3af", textDecoration:"line-through", marginLeft:4 },

  addBtn: { display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#0d6efd,#003c99)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0, boxShadow:"0 4px 14px rgba(13,110,253,0.35)" },
  qtyControl: { display:"flex", alignItems:"center", gap:6, background:"#dbeafe", borderRadius:12, padding:"4px 6px" },
  qtyBtn: { width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", background:"#fff", border:"none", borderRadius:8, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.1)", color:"#0d6efd", fontWeight:700 },
  qtyNum: { minWidth:22, textAlign:"center", fontSize:15, fontWeight:800, color:"#0d6efd" },

  // Trust bar
  trustBar: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px,1fr))", gap:0, background:"#fff", borderTop:"1px solid #ececec" },
  trustItem: { display:"flex", alignItems:"center", gap:14, padding:"22px 28px", borderRight:"1px solid #f3f4f6" },
  trustIcon: { width:48, height:48, borderRadius:14, background:"#eaf2ff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  trustTitle: { margin:"0 0 2px", fontSize:14, fontWeight:800, color:"#111827" },
  trustSub:   { margin:0, fontSize:12, color:"#6b7280" },

  clearFiltersBtn: { padding:"11px 28px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#0d6efd,#003c99)", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", boxShadow:"0 4px 14px rgba(13,110,253,0.3)" },
};

export default ShoppingPage;
